import React, { useMemo, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import idLocale from 'date-fns/locale/id';
import api from '../services/api'; // Assuming you have this service
import { useNotifier } from '../hooks/useNotifier';
import { useModularUnits } from '../pages/projects/gantt-new/hooks/useModularUnits';
import { saveAs } from 'file-saver';

// Note: requires `jszip` dependency. After code update, run: npm i jszip
import JSZip from 'jszip';

// Build absolute URL for attachment if backend returns relative path
function buildAttachmentUrl(fileUrl) {
  if (!fileUrl) return '';
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  if (fileUrl.startsWith('/api/')) {
    const filename = fileUrl.replace(/^\/api\/uploads\//, '').replace(/^\/+/, '');
    return `https://api-dev.prefab-hakiki.id/uploads/${filename}`;
  }
  return `https://api-dev.prefab-hakiki.id/uploads/${fileUrl.replace(/^\/+/, '')}`;
}

function toYMD(date) {
  if (!(date instanceof Date) || isNaN(date)) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatLocaleDate(date) {
  if (!(date instanceof Date) || isNaN(date)) return '-';
  return date.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function parseLocalDate(raw) {
  try {
    if (!raw) return null;
    if (raw instanceof Date) return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
    if (typeof raw === 'string') {
      if (/T/.test(raw)) {
        const dt = new Date(raw);
        if (!isNaN(dt)) return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
      } else {
        const parts = raw.split('-');
        if (parts.length === 3) return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        const dt = new Date(raw);
        if (!isNaN(dt)) return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
      }
    }
    const dt = new Date(raw);
    if (!isNaN(dt)) return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
  } catch (_) { /* ignore */ }
  return null;
}

export default function DownloadProgressDialog({ open, onClose, project }) {
  const { notifySuccess, notifyError, notifyInfo } = useNotifier();
  const { units: modularUnits, loading: unitsLoading } = useModularUnits(project?.id);
  const startDate = useMemo(() => parseLocalDate(project?.startDate), [project]);
  const endDate = useMemo(() => parseLocalDate(project?.estimatedEndDate), [project]);

  const [rangeStart, setRangeStart] = useState(startDate);
  const [rangeEnd, setRangeEnd] = useState(endDate);
  const [selectedUnit, setSelectedUnit] = useState('ALL');
  const [downloading, setDownloading] = useState(false);

  // Keep range locked within project start/end
  const handleStartChange = useCallback((newVal) => {
    if (!newVal) return;
    let v = new Date(newVal.getFullYear(), newVal.getMonth(), newVal.getDate());
    if (startDate && v < startDate) v = startDate;
    if (endDate && v > endDate) v = endDate;
    if (rangeEnd && v > rangeEnd) {
      setRangeEnd(v);
    }
    setRangeStart(v);
  }, [startDate, endDate, rangeEnd]);

  const handleEndChange = useCallback((newVal) => {
    if (!newVal) return;
    let v = new Date(newVal.getFullYear(), newVal.getMonth(), newVal.getDate());
    if (startDate && v < startDate) v = startDate;
    if (endDate && v > endDate) v = endDate;
    if (rangeStart && v < rangeStart) {
      setRangeStart(v);
    }
    setRangeEnd(v);
  }, [startDate, endDate, rangeStart]);

  const canDownload = !!(project && rangeStart && rangeEnd && !downloading && !unitsLoading);

  async function fetchApprovedSubmissions(projectId) {
    const params = new URLSearchParams();
    params.set('projectId', projectId);
    params.set('status', 'approved');
    params.set('limit', '1000');
    const { data } = await api.get(`/progress-submissions?${params.toString()}`);
    return data?.progressSubmissions || [];
  }

  function inRangeYMD(rawDate) {
    const d = parseLocalDate(rawDate);
    if (!d) return false;
    const s = rangeStart; const e = rangeEnd;
    return (!s || d >= s) && (!e || d <= e);
  }

  async function handleDownload() {
    if (!project?.id) return;
    try {
      setDownloading(true);

      // 1) Get approved submissions for this project
      const baseRows = await fetchApprovedSubmissions(project.id);

      // 2) Filter by selected date range based on progressWorkDate/progressworkdate
      let filtered = baseRows.filter(r => inRangeYMD(r.progressWorkDate || r.progressworkdate));
      if (selectedUnit !== 'ALL') {
        const selectedUnitData = modularUnits.find(u => String(u.id) === String(selectedUnit));
        if (selectedUnitData?.name) {
          filtered = filtered.filter(r => (r.modularUnitName || r.modularunitname) === selectedUnitData.name);
        } else {
          // If selected unit not found, result will be empty
          filtered = [];
        }
      }

      // 3) Build ZIP
      const zip = new JSZip();
      const root = zip.folder(project.name || 'project') || zip;

      // Helper: safe filename parts
      const safe = (s) => String(s || '').replace(/[\\/:*?"<>|]/g, '_');

      // 4) Create folders for all relevant modular units first
      const unitsToProcess = selectedUnit === 'ALL'
        ? modularUnits
        : modularUnits.filter(u => String(u.id) === String(selectedUnit));

      unitsToProcess.forEach(unit => {
        if (unit?.name) {
          root.folder(safe(unit.name));
        }
      });

      // 5) For each submission, fetch details and add attachments to the correct folder
      for (const s of filtered) {
        try {
          const { data } = await api.get(`/progress-submissions/${s.id}`);
          const attachments = data?.attachments || [];

          // Compute name parts for folder and file structure
          const mod = s.modularUnitName || s.modularunitname || data?.modularUnitName || data?.modularunitname;
          if (!mod) continue; // Skip if no building name

          const cat = s.workCategoryName || s.workcategoryname || data?.workCategoryName || data?.workcategoryname || 'Kategori';
          const task = s.taskName || s.taskname || data?.taskName || data?.taskname || 'Pekerjaan';
          const ymd = toYMD(parseLocalDate(s.progressWorkDate || s.progressworkdate)) || '-';

          // Create a folder for the building (modular unit)
          const buildingFolder = root.folder(safe(mod)) || root;

          // Download each attachment as ArrayBuffer and add to zip
          for (const a of attachments) {
            const url = buildAttachmentUrl(a.fileUrl || a.url);
            if (!url) continue;
            try {
              const resp = await api.get(url, { responseType: 'arraybuffer' }, { timeout: 30000 });
              // Try to preserve ext from original fileUrl
              const original = (a.originalName || a.filename || a.fileName || a.fileUrl || 'file').toString();
              const extMatch = original.match(/\.[A-Za-z0-9]+$/);
              const ext = extMatch ? extMatch[0] : '';
              // New filename format: work category - task name - progress work date
              const fileName = `${safe(cat)} - ${safe(task)} - ${ymd}${ext}`;
              buildingFolder.file(fileName, resp.data);
            } catch (_) {
              // Skip failed attachment
            }
          }
        } catch (_) {
          // Skip failed submission detail
        }
      }

      // Check if any file was added to the zip
      if (Object.keys(zip.files).length <= 1) { // Only root folder exists
        notifyInfo('Tidak ada lampiran yang ditemukan pada rentang tanggal dan bangunan yang dipilih');
        setDownloading(false);
        return;
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const zipName = `${(project.name || 'project').replace(/\s+/g, '_')}.zip`;
      saveAs(blob, zipName);
      notifySuccess('Download ZIP berhasil dibuat');
      onClose?.();
    } catch (e) {
      notifyError(`Gagal membuat ZIP: ${e?.message || e}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Dialog open={open} onClose={downloading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Download Attachment</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Pilih rentang tanggal untuk mengunduh lampiran progress yang telah di-approve.
        </DialogContentText>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {project?.name || '-'}
          </Typography>

          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarTodayIcon sx={{ color: '#F0C14B', mr: 1 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.6 }}>
                    TANGGAL MULAI
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatLocaleDate(startDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarTodayIcon sx={{ color: '#F0C14B', mr: 1 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.6 }}>
                    TANGGAL SELESAI
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {formatLocaleDate(endDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={downloading || unitsLoading}>
          <InputLabel id="unit-select-label">Pilih Bangunan</InputLabel>
          <Select
            labelId="unit-select-label"
            value={selectedUnit}
            label="Pilih Bangunan"
            onChange={(e) => setSelectedUnit(e.target.value)}
          >
            <MenuItem value="ALL">
              <em>Semua Bangunan</em>
            </MenuItem>
            {unitsLoading ? (
              <MenuItem disabled>Memuat bangunan...</MenuItem>
            ) : (
              modularUnits.map(unit => (
                <MenuItem key={unit.id} value={String(unit.id)}>
                  {unit.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Download File</Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={idLocale}>
          <Grid container spacing={2}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DatePicker
                label="Dari Tanggal"
                value={rangeStart}
                onChange={handleStartChange}
                minDate={startDate || undefined}
                maxDate={endDate || undefined}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <DatePicker
                label="Sampai Tanggal"
                value={rangeEnd}
                onChange={handleEndChange}
                minDate={startDate || undefined}
                maxDate={endDate || undefined}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={downloading}>Tutup</Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          disabled={!canDownload}
          sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}
          startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {downloading ? 'Menyiapkan...' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
